import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Required substring for scripts/validate_dna_blueprints.mjs (runtime writing steps). */
export const DNA_VALIDATE_WRITING_PHRASE = "אני שואל/ת את עצמי בשקט";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Naming the Lack of Closure",
    opening: localizedGenderNode(
      [
      "Today I stop calling this “crazy.” I call it what it is: lack of closure — and I start from there.",
      "I pause with myself."
    ],
      [
      "Today I stop calling this “crazy.” I call it what it is: lack of closure — and I start from there.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One slow breath.",
      "I say quietly inside: “My mind is searching for an answer to feel safe.”"
    ],
      [
      "One slow breath.",
      "I say quietly inside: “My mind is searching for an answer to feel safe.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing — for myself\nI ask myself:\nWhat exactly feels unfinished for me? (What do I feel I still need to understand / hear / see?)",
      "I ask myself:\nWhat am I afraid will happen if I never know “why”?",
      "I ask myself:\nWhat do I keep doing to try to get closure? (texting / checking / replaying / imagining a conversation)"
    ],
      [
      "Writing — for myself\nI ask myself:\nWhat exactly feels unfinished for me? (What do I feel I still need to understand / hear / see?)",
      "I ask myself:\nWhat am I afraid will happen if I never know “why”?",
      "I ask myself:\nWhat do I keep doing to try to get closure? (texting / checking / replaying / imagining a conversation)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Lack of closure is real pain. Today I stop blaming myself for it."
    ],
      [
      "Closing",
      "Lack of closure is real pain. Today I stop blaming myself for it."
    ]
    ),
    action: localizedGenderNode(
      "Once today: one breath + the sentence: “This is lack of closure. I can hold it.”",
      "Once today: one breath + the sentence: “This is lack of closure. I can hold it.”"
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "Story vs. Fact",
    opening: localizedGenderNode(
      [
      "Today I separate what I know from what my mind is trying to complete.",
      "I pause with myself."
    ],
      [
      "Today I separate what I know from what my mind is trying to complete.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Not every gap must close today.”"
    ],
      [
      "One breath. “Not every gap must close today.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat are the dry facts I actually know? (2–4 lines, no interpretation)",
      "What don’t I know — that I try to fill with imagination?",
      "What story does my pain tell about what I don’t know?",
      "One gentle truth I can choose today instead of the story:"
    ],
      [
      "Writing\nWhat are the dry facts I actually know? (2–4 lines, no interpretation)",
      "What don’t I know — that I try to fill with imagination?",
      "What story does my pain tell about what I don’t know?",
      "One gentle truth I can choose today instead of the story:"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I can live with “I don’t know” without letting it run my life."
    ],
      [
      "Closing",
      "I can live with “I don’t know” without letting it run my life."
    ]
    ),
    action: localizedGenderNode(
      "Once today: “I return to the facts.” Then one breath.",
      "Once today: “I return to the facts.” Then one breath."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "The Urge to Ask or Search (90-Second Protocol)",
    opening: localizedGenderNode(
      [
      "Today I build a pause before every action that tries to force closure.",
      "I pause with myself."
    ],
      [
      "Today I build a pause before every action that tries to force closure.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I choose myself first.”"
    ],
      [
      "One breath. “I choose myself first.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat urge comes up today? (texting / asking others / checking / analyzing)",
      "What am I trying to soothe through this urge? (uncertainty / pain / humiliation / fear of abandonment)",
      "90-Second Protocol (from today onward):\n* 3 slow breaths\n* Notice the body — where the urge sits\n* One sentence inside: “This is an urge. It will pass.”\nThen choose one small alternative.\n\nWhich alternative do I choose today instead of searching for answers?"
    ],
      [
      "Writing\nWhat urge comes up today? (texting / asking others / checking / analyzing)",
      "What am I trying to soothe through this urge? (uncertainty / pain / humiliation / fear of abandonment)",
      "90-Second Protocol (from today onward):\n* 3 slow breaths\n* Notice the body — where the urge sits\n* One sentence inside: “This is an urge. It will pass.”\nThen choose one small alternative.\n\nWhich alternative do I choose today instead of searching for answers?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I don’t need action to calm down. I can calm down without answers."
    ],
      [
      "Closing",
      "I don’t need action to calm down. I can calm down without answers."
    ]
    ),
    action: localizedGenderNode(
      "Every time an urge appears — I do the protocol once only.",
      "Every time an urge appears — I do the protocol once only."
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "What I’m Really Seeking in “Closure”",
    opening: localizedGenderNode(
      [
      "Today I discover that what I’m seeking is deeper than information.",
      "I pause with myself."
    ],
      [
      "Today I discover that what I’m seeking is deeper than information.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Maybe I’m not seeking facts — maybe I’m seeking peace.”"
    ],
      [
      "One breath. “Maybe I’m not seeking facts — maybe I’m seeking peace.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nIf I got the “perfect answer,” what would I feel for a moment?",
      "What need hides there? (respect / validation / understanding / justice / belonging / safety)",
      "How can I give myself 10% of that need today — without the other person?"
    ],
      [
      "Writing\nIf I got the “perfect answer,” what would I feel for a moment?",
      "What need hides there? (respect / validation / understanding / justice / belonging / safety)",
      "How can I give myself 10% of that need today — without the other person?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Sometimes closure is not what they say — it’s what I give myself."
    ],
      [
      "Closing",
      "Sometimes closure is not what they say — it’s what I give myself."
    ]
    ),
    action: localizedGenderNode(
      "I choose one need and give it a small form today (safe conversation / walk / order / music).",
      "I choose one need and give it a small form today (safe conversation / walk / order / music)."
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "pattern",
    title: "The Pain of Not Being Respected",
    opening: localizedGenderNode(
      [
      "Today I touch the place where I wasn’t treated with respect.",
      "I pause with myself."
    ],
      [
      "Today I touch the place where I wasn’t treated with respect.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I’m allowed to hurt over missing respect.”"
    ],
      [
      "One breath. “I’m allowed to hurt over missing respect.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat respect was missing for me in the relationship or ending?",
      "What sentence did I need to hear — and didn’t?",
      "What sentence of respect can I say to myself today instead?"
    ],
      [
      "Writing\nWhat respect was missing for me in the relationship or ending?",
      "What sentence did I need to hear — and didn’t?",
      "What sentence of respect can I say to myself today instead?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "If I didn’t receive respect from outside — I begin giving it from within."
    ],
      [
      "Closing",
      "If I didn’t receive respect from outside — I begin giving it from within."
    ]
    ),
    action: localizedGenderNode(
      "I write one sentence of self-respect and keep it in my journal.",
      "I write one sentence of self-respect and keep it in my journal."
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "fear",
    title: "Guilt and Self-Blame",
    opening: localizedGenderNode(
      [
      "Today I stop whipping myself in the name of “explanations.”",
      "I pause with myself."
    ],
      [
      "Today I stop whipping myself in the name of “explanations.”",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Taking responsibility does not mean breaking myself.”"
    ],
      [
      "One breath. “Taking responsibility does not mean breaking myself.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat guilt story do I replay to explain what happened?",
      "What small part is truly mine — and what is not?",
      "How can I say the same thing in a softer tone?"
    ],
      [
      "Writing\nWhat guilt story do I replay to explain what happened?",
      "What small part is truly mine — and what is not?",
      "How can I say the same thing in a softer tone?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Responsibility without compassion doesn’t close loops — it opens wounds."
    ],
      [
      "Closing",
      "Responsibility without compassion doesn’t close loops — it opens wounds."
    ]
    ),
    action: localizedGenderNode(
      "When guilt appears — I return to my soft sentence once only.",
      "When guilt appears — I return to my soft sentence once only."
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "need",
    title: "The Letter That Is Not Sent",
    opening: localizedGenderNode(
      [
      "Today I give my mind an ending — even without a real conversation.",
      "I pause with myself."
    ],
      [
      "Today I give my mind an ending — even without a real conversation.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I can write closure even if I didn’t receive it.”"
    ],
      [
      "One breath. “I can write closure even if I didn’t receive it.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nA letter not sent (6–10 lines):\nWhat do I truly want to say — without apologizing or attacking?",
      "Which one sentence is the heart of this letter?",
      "What do I release today from the need that “they understand”?"
    ],
      [
      "Writing\nA letter not sent (6–10 lines):\nWhat do I truly want to say — without apologizing or attacking?",
      "Which one sentence is the heart of this letter?",
      "What do I release today from the need that “they understand”?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Closure doesn’t always come from outside. Sometimes it’s an internal act of respect."
    ],
      [
      "Closing",
      "Closure doesn’t always come from outside. Sometimes it’s an internal act of respect."
    ]
    ),
    action: localizedGenderNode(
      "I read the letter once — then close it. No more analysis.",
      "I read the letter once — then close it. No more analysis."
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "softening",
    title: "Gentle Acceptance (ACT)",
    opening: localizedGenderNode(
      [
      "Today I practice holding uncertainty for a moment — without running.",
      "I pause with myself."
    ],
      [
      "Today I practice holding uncertainty for a moment — without running.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “This is uncertainty. And it’s survivable.”"
    ],
      [
      "One breath. “This is uncertainty. And it’s survivable.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat trigger most activates my need for closure?",
      "One-minute practice: I let “not knowing” be here for one minute, without action.\nWhat happened in my body after the minute?",
      "What did I learn? Did the wave really not pass?"
    ],
      [
      "Writing\nWhat trigger most activates my need for closure?",
      "One-minute practice: I let “not knowing” be here for one minute, without action.\nWhat happened in my body after the minute?",
      "What did I learn? Did the wave really not pass?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I grow stronger not because I got an answer — but because I stayed with the question."
    ],
      [
      "Closing",
      "I grow stronger not because I got an answer — but because I stayed with the question."
    ]
    ),
    action: localizedGenderNode(
      "One minute only. Consistency > force.",
      "One minute only. Consistency > force."
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "softening",
    title: "Releasing the Fantasy Conversation",
    opening: localizedGenderNode(
      [
      "Today I let go of the scene my mind keeps staging.",
      "I pause with myself."
    ],
      [
      "Today I let go of the scene my mind keeps staging.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Scenes in my head don’t give me my life back.”"
    ],
      [
      "One breath. “Scenes in my head don’t give me my life back.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat does the perfect conversation look like in my mind?",
      "What am I trying to receive there? (recognition / apology / explanation / choosing me)",
      "What small piece can I give myself today instead?"
    ],
      [
      "Writing\nWhat does the perfect conversation look like in my mind?",
      "What am I trying to receive there? (recognition / apology / explanation / choosing me)",
      "What small piece can I give myself today instead?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I release the inner stage and return energy to my life."
    ],
      [
      "Closing",
      "I release the inner stage and return energy to my life."
    ]
    ),
    action: localizedGenderNode(
      "Once today, when I stage a conversation — I say “Not now” and return to my breath.",
      "Once today, when I stage a conversation — I say “Not now” and return to my breath."
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "perspective",
    title: "Boundaries With Myself",
    opening: localizedGenderNode(
      [
      "Today I place one boundary that protects me from reopening the wound.",
      "I pause with myself."
    ],
      [
      "Today I place one boundary that protects me from reopening the wound.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “A boundary is protection.”"
    ],
      [
      "One breath. “A boundary is protection.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhich action opens the wound most?",
      "What small boundary do I set for this week?\n(“This week I will not _ to protect myself.”)",
      "What fear arises if I hold this boundary?",
      "What gentle answer can I give that fear?"
    ],
      [
      "Writing\nWhich action opens the wound most?",
      "What small boundary do I set for this week?\n(“This week I will not _ to protect myself.”)",
      "What fear arises if I hold this boundary?",
      "What gentle answer can I give that fear?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I’m not punishing myself. I’m protecting myself."
    ],
      [
      "Closing",
      "I’m not punishing myself. I’m protecting myself."
    ]
    ),
    action: localizedGenderNode(
      "I remove or hide one trigger (chat / photo / social media) that ignites the urge.",
      "I remove or hide one trigger (chat / photo / social media) that ignites the urge."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "strength",
    title: "Restoring Control",
    opening: localizedGenderNode(
      [
      "Today I shrink the storm to one step.",
      "I pause with myself."
    ],
      [
      "Today I shrink the storm to one step.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “One step is enough.”"
    ],
      [
      "One breath. “One step is enough.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat overwhelms me most today around lack of closure?",
      "What 3 very small things are in my control today?",
      "Which one step will I take today?"
    ],
      [
      "Writing\nWhat overwhelms me most today around lack of closure?",
      "What 3 very small things are in my control today?",
      "Which one step will I take today?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing"
    ],
      [
      "Closing"
    ]
    ),
    action: localizedGenderNode(
      "Small action for today",
      "Small action for today"
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "release",
    title: "Night Loops",
    opening: localizedGenderNode(
      [
      "Today I give my mind an end-of-day signal.",
      "I pause with myself."
    ],
      [
      "Today I give my mind an end-of-day signal.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “Tonight I don’t need to solve.”"
    ],
      [
      "One breath. “Tonight I don’t need to solve.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is my most common night loop?",
      "What is the loop trying to achieve for me?",
      "What closing sentence do I choose for tonight?",
      "What 3-minute ritual will help before sleep?"
    ],
      [
      "Writing\nWhat is my most common night loop?",
      "What is the loop trying to achieve for me?",
      "What closing sentence do I choose for tonight?",
      "What 3-minute ritual will help before sleep?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "A tired mind tells powerful stories. Tonight I choose rest."
    ],
      [
      "Closing",
      "A tired mind tells powerful stories. Tonight I choose rest."
    ]
    ),
    action: localizedGenderNode(
      "Before sleep: my closing sentence + one breath. That’s all.",
      "Before sleep: my closing sentence + one breath. That’s all."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "choice",
    title: "Turning the Open Ending Into Direction",
    opening: localizedGenderNode(
      [
      "Today the “unfinished” becomes a compass.",
      "I pause with myself."
    ],
      [
      "Today the “unfinished” becomes a compass.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “If this hurt — it teaches me.”"
    ],
      [
      "One breath. “If this hurt — it teaches me.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat does this teach me about what I need in future relationships or life?",
      "Which one value will lead me now?\n(respect / truth / calm / stability)",
      "What small habit moves me toward that value this week?",
      "What promise do I make myself when the urge to force closure appears?"
    ],
      [
      "Writing\nWhat does this teach me about what I need in future relationships or life?",
      "Which one value will lead me now?\n(respect / truth / calm / stability)",
      "What small habit moves me toward that value this week?",
      "What promise do I make myself when the urge to force closure appears?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I don’t need to stay stuck in the past to build a future."
    ],
      [
      "Closing",
      "I don’t need to stay stuck in the past to build a future."
    ]
    ),
    action: localizedGenderNode(
      "I plan one small thing this week that restores a sense of future.",
      "I plan one small thing this week that restores a sense of future."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "Gentle Closing Ritual",
    opening: localizedGenderNode(
      [
      "Today I close a chapter without erasing it. I stay with myself.",
      "I pause with myself."
    ],
      [
      "Today I close a chapter without erasing it. I stay with myself.",
      "I pause with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "One breath. “I release without disappearing.”"
    ],
      [
      "One breath. “I release without disappearing.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nThree things I stop doing to protect my heart:",
      "Three things I begin doing to build myself:",
      "A return sentence for hard days (short, clear):"
    ],
      [
      "Writing\nThree things I stop doing to protect my heart:",
      "Three things I begin doing to build myself:",
      "A return sentence for hard days (short, clear):"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Final Closing",
      "I may never get the answer I wanted.",
      "But I can get my life back.",
      "I keep walking —",
      "slowly, quietly, with myself.",
      "And that is strength."
    ],
      [
      "Final Closing",
      "I may never get the answer I wanted.",
      "But I can get my life back.",
      "I keep walking —",
      "slowly, quietly, with myself.",
      "And that is strength."
    ]
    ),
    action: localizedGenderNode(
      "I choose one action that symbolizes a “new chapter”",
      "I choose one action that symbolizes a “new chapter”"
    ),
  },
].map(buildNormalizedDay);

export const lackOfClosureAfterBreakupBlueprintEn = buildNormalizedPilotBlueprint({
  id: "lack_of_closure_after_breakup_14d",
  version: "tri_locale_emit_en_v1",
  durationDays: 14,
  problemKey: "lack_of_closure_after_breakup",
  category: "general",
  journeyName: {
    he: "חוסר סגירה ופרידה לא פתורה",
    en: "Lack of Closure & Unresolved Separation",
    es: "Falta de cierre y separación no resuelta",
  },
  days,
});

export default lackOfClosureAfterBreakupBlueprintEn;
